import { useEffect, useState } from "react";
import { ResourceWrapper } from "../../../eventer-pod/client/ResourceWrapper.ts";
import { LocalEventerPod } from "../../../eventer-pod/client/local-eventer-pod.ts";
import Session from "../../../appfw/authentication/Session.ts";

/**
 * Hook to load the first linked resource from a property
 * 
 * @param pod - The LocalEventerPod instance
 * @param session - The current session (undefined = loading, null = public access, Session = authenticated)
 * @param resource - The main resource to load the property from
 * @param propertyUri - The property URI to load
 * @returns The first linked resource with 3-state semantics:
 *   - `undefined`: Still loading (session loading or resource loading)
 *   - `null`: Resource does not exist or property has no value
 *   - `ResourceWrapper`: Successfully loaded resource
 * 
 * **Session States:**
 * - `undefined`: Session is still loading → hook waits, returns undefined
 * - `null`: User is not authenticated → hook loads public resource with sessionId=undefined
 * - `Session`: User is authenticated → hook loads resource with user's session
 * 
 * @example
 * const image = useLinkedResource(pod, session, event, 'https://schema.org/image');
 * const location = useLinkedResource(pod, session, event, 'https://schema.org/location');
 * 
 * if (image === undefined) {
 *   // Still loading...
 * } else if (image === null) {
 *   // No image exists
 * } else {
 *   const imageUrl = image.getFirstString('https://schema.org/contentUrl');
 * }
 */
export function useLinkedResource(
    pod: LocalEventerPod | undefined,
    session: Session | null | undefined,
    resource: ResourceWrapper | undefined,
    propertyUri: string
): ResourceWrapper | null | undefined {

    const [linkedResource, setLinkedResource] = useState<ResourceWrapper | null | undefined>(undefined)

    useEffect(() => {
        // If pod or session is undefined (still loading), wait and return undefined (loading state)
        if (!pod || session === undefined) {
            setLinkedResource(undefined)
            return
        }

        // If resource is undefined, can't load anything - return null (does not exist)
        if (!resource) {
            setLinkedResource(null)
            return
        }

        // Get the first URI for this property
        const uri = resource.getFirstString(propertyUri)

        if (!uri) {
            // No URI found for this property - return null (does not exist)
            setLinkedResource(null)
            return
        }

        // Start loading - set to undefined (loading state)
        setLinkedResource(undefined)

        let cancelled = false
        const sessionId = session?.id

        // Load the resource
        pod.getResource({
            uri,
            sessionId,
            importOptions: { ifMissing: true }
        })
            .then((loadedResource) => {
                if (!cancelled) {
                    // Set to null if not found, otherwise the loaded resource
                    setLinkedResource(loadedResource || null)
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error(`[use-linked-resource] Failed to load ${propertyUri}:`, err)
                    // On error, set to null (does not exist / failed to load)
                    setLinkedResource(null)
                }
            })

        return () => {
            cancelled = true
        }
    }, [pod, session, resource, propertyUri])

    return linkedResource
}
